import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useCloseActionMenu } from 'components/ActionMenu/ActionMenuProvider';

const useStyles = createUseStyles(() => ({
  container: {
    padding: 8
  },
  select: {
    display: 'flex',
    padding: 10,
    borderRadius: 6,
    cursor: 'pointer',
    '&:hover': {
      background: '#f7f7f8'
    }
  },
  label: {
    marginLeft: 10,
    fontSize: 14,
    fontWeight: 400
  },
  sectionTitle: {
    color: '#6A728F',
    fontSize: 12,
    marginTop: 4,
    marginLeft: 12,
    marginBottom: 10
  },
  categorySelect: {
    margin: '0 10px 6px',
    padding: '8px 10px',
    width: 'calc(100% - 20px)',
    boxSizing: 'border-box',
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer'
  },
  disabled: {
    opacity: 0.45,
    cursor: 'default',
    '&:hover': {
      background: 'none'
    }
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: 8,
    padding: '8px 10px 4px',
    borderTop: '1px solid #ededed'
  },
  applyButton: {
    flexGrow: 1,
    padding: '8px 14px',
    background: '#3e55f6',
    color: 'white',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      background: '#3549d4'
    }
  },
  clearAll: {
    padding: '8px 10px',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    color: '#6A728F',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: '#f7f7f8'
    }
  }
}));

type State = {
  allComplete: boolean;
  sixMonths: boolean;
  threeMonths: boolean;
  incompleteCourse: boolean;
  hasExpiredDocument: boolean;
  hasActiveDocument: boolean;
  hasActiveBackgroundCheck: boolean;
  hasExpiredBackgroundCheck: boolean;
  secondaryCategory: string;
  hasActiveCert: boolean;
  hasExpiredCert: boolean;
};

type CertCategory = {
  uuid: string;
  name: string;
};

type Props = {
  onChange?: (state: State) => void;
  state: State;
  certCategories?: CertCategory[];
};

const COURSE_OPTIONS: { key: keyof State; label: string }[] = [
  { key: 'allComplete', label: 'All courses completed' },
  { key: 'incompleteCourse', label: 'Has incomplete course' },
  { key: 'sixMonths', label: 'Expiring in 6 months' },
  { key: 'threeMonths', label: 'Expiring in 3 months' },
  { key: 'hasExpiredDocument', label: 'Has expired document' },
  { key: 'hasActiveDocument', label: 'Has active document' },
  { key: 'hasActiveBackgroundCheck', label: 'Has valid background check' },
  { key: 'hasExpiredBackgroundCheck', label: 'Has expired background check' }
];

const CLEARED: State = {
  allComplete: false,
  sixMonths: false,
  threeMonths: false,
  incompleteCourse: false,
  hasExpiredDocument: false,
  hasActiveDocument: false,
  hasActiveBackgroundCheck: false,
  hasExpiredBackgroundCheck: false,
  secondaryCategory: '',
  hasActiveCert: false,
  hasExpiredCert: false
};

// Draft state lives locally so several filters can be picked in one visit;
// nothing is applied (and the menu stays open) until Apply is clicked.
// Clicking the backdrop still closes the menu, discarding the draft.
export default function DelegatesFilterOptions({
  state,
  onChange,
  certCategories = []
}: Props) {
  const classes = useStyles();
  const closeMenu = useCloseActionMenu();
  const [draft, setDraft] = useState<State>(state);

  // Only one of the course/document/background-check filters can be active
  const toggleOption = (key: keyof State) => {
    setDraft({
      ...CLEARED,
      secondaryCategory: draft.secondaryCategory,
      hasActiveCert: draft.hasActiveCert,
      hasExpiredCert: draft.hasExpiredCert,
      [key]: !draft[key]
    });
  };

  const anyActive =
    COURSE_OPTIONS.some((option) => !!draft[option.key]) ||
    !!draft.secondaryCategory;

  return (
    <div
      className={classes.container}
      // The menu's backdrop closes it on any bubbled click; keep the menu
      // open while filters are being composed
      onClick={(evt) => evt.stopPropagation()}
    >
      <div className={classes.sectionTitle}>FILTER</div>
      {COURSE_OPTIONS.map((option) => (
        <div
          key={option.key}
          className={classes.select}
          onClick={() => toggleOption(option.key)}
        >
          <input type="checkbox" checked={!!draft[option.key]} readOnly />
          <div className={classes.label}>{option.label}</div>
        </div>
      ))}

      <div className={classes.sectionTitle}>CERTIFICATES</div>
      <select
        className={classes.categorySelect}
        value={draft.secondaryCategory}
        onChange={(evt) => {
          const secondaryCategory = evt.target.value;
          setDraft({
            ...draft,
            secondaryCategory,
            hasActiveCert: secondaryCategory ? draft.hasActiveCert : false,
            hasExpiredCert: secondaryCategory ? draft.hasExpiredCert : false
          });
        }}
      >
        <option value="">All categories</option>
        {certCategories.map((category) => (
          <option key={category.uuid} value={category.uuid}>
            {category.name}
          </option>
        ))}
      </select>
      <div
        className={`${classes.select}${
          !draft.secondaryCategory ? ` ${classes.disabled}` : ''
        }`}
        title={
          !draft.secondaryCategory ? 'Select a certificate category first' : ''
        }
        onClick={() => {
          draft.secondaryCategory &&
            setDraft({
              ...draft,
              hasActiveCert: !draft.hasActiveCert,
              hasExpiredCert: false
            });
        }}
      >
        <input type="checkbox" checked={draft.hasActiveCert} readOnly />
        <div className={classes.label}>Has active certificate</div>
      </div>
      <div
        className={`${classes.select}${
          !draft.secondaryCategory ? ` ${classes.disabled}` : ''
        }`}
        title={
          !draft.secondaryCategory ? 'Select a certificate category first' : ''
        }
        onClick={() => {
          draft.secondaryCategory &&
            setDraft({
              ...draft,
              hasExpiredCert: !draft.hasExpiredCert,
              hasActiveCert: false
            });
        }}
      >
        <input type="checkbox" checked={draft.hasExpiredCert} readOnly />
        <div className={classes.label}>Has expired certificate</div>
      </div>

      <div className={classes.footer}>
        <div
          className={classes.applyButton}
          onClick={() => {
            onChange && onChange(draft);
            closeMenu();
          }}
        >
          Apply
        </div>
        {anyActive && (
          <div className={classes.clearAll} onClick={() => setDraft(CLEARED)}>
            Clear all
          </div>
        )}
      </div>
    </div>
  );
}
